import { useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChallengeService } from '@duck/api-definitions'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Button,
} from '@duck/ui'
import { apis } from '@modules/apiClients'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'
import { useTar } from '@/modules'
import { useAtomValue } from 'jotai'
import { currentSolutionAtom } from '@/store/saveWorkAtoms'
import DOMPurify from 'dompurify'
import { TriangleAlert } from 'lucide-react'
import { WorkspaceLayoutToggle } from './WorkspaceLayoutToggle'
import { useQueryClient } from '@tanstack/react-query'
import { ongoingChallengesQueryKeys } from '@/hooks/useOngoingChallenges'

interface WorkspaceStatusPaneProps {
    challenge: ChallengeService.Challenge
    hasCharts: boolean
}

const cleanSolution = (solution: string) => {
    return DOMPurify.sanitize(solution, { ALLOWED_TAGS: [] }).trim()
}

const defaultSolution = `<h2>Data Sanity</h2><div><i>(Write your checks — what's broken, what's missing, what's weird?)</i></div><div><br></div><h2>Exploration &amp; Hypotheses</h2><div><i>(What patterns or relationships are you looking for?)</i></div><div><br></div><h2>Main Analysis</h2><div><i>(Explain your reasoning — connect evidence to insights)</i></div><div><br></div><h2>Interpretation &amp; Conclusions</h2><div><i>(What would you recommend the stakeholder?)</i></div><div><br></div><h2>Next Steps / Limitations</h2><div><i>(“If I had more data, I'd check…” / “This trend might be biased because…”)</i></div>`
const sqlQueryRegex = /SELECT\s.*FROM\s/i

export const WorkspaceStatusPane = ({ challenge, hasCharts }: WorkspaceStatusPaneProps) => {
    const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { t } = useTar()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const currentSolution = useAtomValue(currentSolutionAtom)
    const isSubmittingRef = useRef(false)

    const handleSubmit = () => {
        const cleanedSolution = cleanSolution(currentSolution.trim() || '')
        if (!cleanedSolution || currentSolution === defaultSolution) {
            toast.error(t('challenges.noSolutionProvidedYet'))
            return
        }
        if (cleanedSolution.length < 50) {
            toast.error(t('challenges.solutionTooShortForMeaningfulFeedback'))
            return
        }
        if (!sqlQueryRegex.test(cleanedSolution) && !hasCharts) {
            toast.error(t('challenges.solutionContainsNoSQL'), { duration: 8000 })
        }
        setIsSubmitDialogOpen(true)
    }

    const handleConfirmSubmit = async () => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            await apis.challengeService.saveWork({
                challengeId: challenge.id,
                workState: {
                    solution: currentSolution?.trim() || '',
                },
            })

            const response = await apis.challengeService.submitSolution({ challengeId: challenge.id })

            if (response.data.success) {
                await queryClient.invalidateQueries({ queryKey: ongoingChallengesQueryKeys.all() })
                toast.success(t('challenges.solutionSubmittedSuccessfully'))
                navigate({ to: '/challenges/completed' })
            } else {
                toast.error(t('challenges.failedToSubmitSolution'))
            }

            setIsSubmitDialogOpen(false)
        } catch (error) {
            console.error('Failed to submit solution:', error)
            toast.error(t('challenges.failedToSubmitSolution'))
            setIsSubmitDialogOpen(false)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="flex w-full p-2 items-center justify-center h-12 bg-card">
            <div className="flex flex-1 items-center justify-between">
                <WorkspaceLayoutToggle />
                <Button
                    onClick={handleSubmit}
                    size="sm"
                    variant="outline"
                    className="px-24 text-foreground border-primary/70 h-8"
                    data-tour-id="submit-solution"
                >
                    {t('common.submit')}
                </Button>
            </div>
            <Dialog
                open={isSubmitDialogOpen}
                onOpenChange={(open) => !isSubmitting && !open && setIsSubmitDialogOpen(false)}
            >
                <DialogContent
                    width="sm"
                    noMinHeight
                    onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
                    onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
                    onInteractOutside={(e) => isSubmitting && e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{t('challenges.submitSolution')}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <p className="text-md">
                            {t('challenges.submitSolutionDescription')}
                        </p>
                        {!challenge.assignmentId && <div className="bg-warning/10 text-warning border border-warning/20 rounded-lg p-3 flex items-start gap-2">
                            <TriangleAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <p className="text-xs">
                                {t('challenges.submissionCooldownDescription')}
                            </p>
                        </div>}

                        <div className="flex gap-3 justify-end pt-4">
                            <Button
                                onClick={() => setIsSubmitDialogOpen(false)}
                                disabled={isSubmitting}
                                variant="outline"
                                size="sm"
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={handleConfirmSubmit}
                                disabled={isSubmitting}
                                variant="default"
                                size="sm"
                            >
                                {isSubmitting && (
                                    <StandardLoader size="sm" />
                                )}
                                {isSubmitting ? t('common.submitting') : t('common.confirmSubmit')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}


